import React from "react";
import { motion } from "framer-motion";

/**
 * WhatAreGreenGasTurbines – Long-form editorial content section
 * Provides 800+ words of unique, educational content about green gas turbines.
 * This is critical for AdSense approval (unique high-quality content requirement)
 * and for ranking on the "green gas turbines" keyword.
 */
export default function WhatAreGreenGasTurbines() {
  return (
    <section id="what-are-green-gas-turbines" className="py-20 bg-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-secondary-900 mb-8">
            What Are Green Gas Turbines?
          </h2>

          <div className="prose prose-lg prose-secondary max-w-none text-secondary-700 leading-relaxed space-y-6">
            <p>
              A <strong>green gas turbine</strong> is a combustion turbine specifically engineered to
              generate electricity and useful heat from low-carbon or zero-carbon fuels—primarily{" "}
              <strong>hydrogen (H₂)</strong>, <strong>renewable natural gas (RNG)</strong>,{" "}
              <strong>biogas</strong>, and <strong>ammonia</strong>—while achieving dramatically lower
              greenhouse-gas and pollutant emissions than conventional natural-gas or diesel-fired
              turbines. The term "green" refers not to the physical colour of the machine, but to its
              environmental profile: when fuelled by green hydrogen produced from renewable
              electrolysis, a green gas turbine emits <em>zero CO₂</em> at the point of combustion.
            </p>

            <p>
              Gas turbines have been the backbone of dispatchable power generation for over sixty
              years. They start in minutes, ramp quickly to follow load, and deliver high power
              density in a compact footprint. However, traditional models burning fossil methane
              release roughly <strong>0.4–0.5 tonnes of CO₂ per MWh</strong>. As the global energy
              transition accelerates, operators face a stark choice: retire these assets early—writing
              off billions in capital—or retrofit and replace them with turbines capable of burning
              cleaner fuels. Green gas turbines offer the second path, preserving the operational
              advantages of gas-fired generation while slashing its carbon intensity by{" "}
              <strong>50–100%</strong> depending on the fuel blend.
            </p>

            <h3 className="text-2xl font-bold text-secondary-900 mt-10 mb-4">
              The Role of Green Gas Turbines in the Energy Transition
            </h3>
            <p>
              Renewable energy sources like wind and solar are intermittent—they produce power only
              when the wind blows or the sun shines. Grid operators need <em>firm, dispatchable</em>{" "}
              capacity to fill the gaps, especially during evening demand peaks and multi-day weather
              events (known as <em>Dunkelflauten</em> in energy planning). Battery storage addresses
              short-duration gaps of 2–4 hours, but longer outages of 8–72 hours require a different
              solution.
            </p>
            <p>
              Green gas turbines fill this critical niche. A hydrogen-fuelled peaker plant can sit
              idle for weeks, then start in under ten minutes and deliver hundreds of megawatts for as
              long as fuel is available. Unlike batteries, turbines do not degrade with deep cycling
              and can provide both electricity and high-grade process heat through{" "}
              <strong>combined heat and power (CHP)</strong> configurations, pushing total fuel
              utilisation above <strong>90%</strong>.
            </p>

            <h3 className="text-2xl font-bold text-secondary-900 mt-10 mb-4">
              Key Fuel Pathways for Green Gas Turbines
            </h3>
            <p>
              The "green" in green gas turbines comes from the fuel, not just the hardware. Several
              low-carbon fuel pathways are commercially available today:
            </p>
            <ul className="list-disc pl-6 space-y-3">
              <li>
                <strong>Green Hydrogen</strong> — Produced by splitting water with renewable
                electricity (electrolysis). Combustion yields only water vapour and trace NOx. Current
                production cost: $3–6/kg, projected to fall below $2/kg by 2030 as electrolyser
                capacity scales.
              </li>
              <li>
                <strong>Renewable Natural Gas (RNG)</strong> — Methane captured from landfills, dairy
                farms, or wastewater treatment plants. Chemically identical to fossil natural gas, so
                it drops into existing infrastructure with zero hardware changes. Lifecycle CO₂
                savings of 70–90% versus fossil gas.
              </li>
              <li>
                <strong>Biogas</strong> — Raw anaerobic-digestion gas (typically 55–65% methane, 35–45%
                CO₂). Requires scrubbing before turbine use but is abundant and low-cost in
                agricultural regions.
              </li>
              <li>
                <strong>Ammonia (NH₃)</strong> — An emerging turbine fuel with high energy density and
                no carbon atoms. Challenges include NOx management and cracking efficiency, but
                several OEMs have demonstrated 100% ammonia combustion in pilot units.
              </li>
            </ul>

            <h3 className="text-2xl font-bold text-secondary-900 mt-10 mb-4">
              Why Green Gas Turbines Matter for Industry and Investors
            </h3>
            <p>
              For <strong>industrial operators</strong>, green gas turbines protect against carbon
              pricing risk. The EU Emissions Trading System (EU ETS) carbon price exceeded €90/tonne
              in 2023, and similar mechanisms are expanding in North America, the UK, and Asia-Pacific.
              A 50 MW combined-cycle plant burning fossil gas faces annual carbon costs of{" "}
              <strong>$8–12 million</strong> at current EU prices. Switching to a 60% hydrogen blend
              cuts that liability by more than half, while also qualifying for clean-energy tax credits
              such as the US Inflation Reduction Act's Section 45V hydrogen production credit.
            </p>
            <p>
              For <strong>investors and project developers</strong>, green gas turbines offer bankable
              returns with de-risked technology. Unlike early-stage fusion or advanced nuclear, gas
              turbines are a mature platform with decades of operational data. The incremental cost of
              hydrogen-readiness adds only 10–15% to the capital expenditure of a new-build unit, yet
              it future-proofs the asset against regulatory obsolescence for its full 25–30 year
              design life.
            </p>

            <h3 className="text-2xl font-bold text-secondary-900 mt-10 mb-4">
              Green Gas Turbines by the Numbers
            </h3>
            <p>
              The global installed base of gas turbines exceeds <strong>800 GW</strong>. The
              International Energy Agency (IEA) estimates that 30–40% of this fleet will need to be
              hydrogen-capable by 2035 to meet net-zero targets. Major OEMs—including GE Vernova,
              Siemens Energy, and Mitsubishi Power—have committed to offering 100% hydrogen-ready
              models across their product lines by 2030. Meanwhile, the green hydrogen market is
              projected to grow from $4.2 billion in 2023 to over $90 billion by 2035, driven by
              falling electrolyser costs and government mandates.
            </p>
            <p>
              At GreenGasTurbines.com, we track these developments daily—publishing technical
              analyses, retrofit guides, and interactive engineering tools to help operators,
              engineers, and investors navigate the transition to low-carbon gas-fired power.
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
